import { motion, useReducedMotion } from "framer-motion";
import { Bot, Check, ChevronLeft, ChevronRight, Crown, Gem, Minus, ShieldCheck, Star, Zap } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CancelDowngradeDialog } from "@/components/subscription/CancelDowngradeDialog";
import { cn } from "@/lib/utils";
import { useSubscription, type PlanId } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { trackCTAClick, trackPricingInteraction } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { CarouselArrow, CarouselDots, CARD_SHADOW_POPULAR } from "./carousel-ui";

/**
 * Tier theme system — each plan gets a distinct, premium visual identity.
 * Free   → muted neutral   (entry point, low desire)
 * Starter→ signature orange (popular tier — confident, energetic)
 * Pro    → warm gold/amber (status, monetization)
 * Business → deep violet  (luxury / concierge / AI flagship)
 */
const TIER_THEME = {
  free: {
    accent: "#9a8a7d",
    chipBg: "bg-[#f1e9e0]",
    chipText: "text-[#9a8a7d]",
    iconColor: "text-[#9a8a7d]",
    Icon: Star,
    eyebrow: "text-[#9a8a7d]",
    aiChipBg: "bg-[#f1e9e0]",
    aiChipText: "text-[#7a6a5f]",
    rail: "border-[#eadbcc]",
  },
  starter: {
    accent: "#ef7b43",
    chipBg: "bg-[#ffe6d3]",
    chipText: "text-[#ef7b43]",
    iconColor: "text-[#ef7b43]",
    Icon: Zap,
    eyebrow: "text-[#ef7b43]",
    aiChipBg: "bg-[#fff1e4]",
    aiChipText: "text-[#c25b27]",
    rail: "border-[#ef7b43]",
  },
  pro: {
    accent: "#b8842a",
    chipBg: "bg-[#fbeed1]",
    chipText: "text-[#a87324]",
    iconColor: "text-[#b8842a]",
    Icon: Crown,
    eyebrow: "text-[#a87324]",
    aiChipBg: "bg-[#fbeed1]",
    aiChipText: "text-[#a87324]",
    rail: "border-[#b8842a]",
  },
  business: {
    accent: "#5b46c4",
    chipBg: "bg-[#ece6ff]",
    chipText: "text-[#5b46c4]",
    iconColor: "text-[#5b46c4]",
    Icon: Gem,
    eyebrow: "text-[#5b46c4]",
    aiChipBg: "bg-[#ece6ff]",
    aiChipText: "text-[#4a37a3]",
    rail: "border-[#5b46c4]",
  },
} as const;
import {
  type BillingPeriod,
  formatAnnualSavings,
  formatAnnualTotal,
  formatMonthlyOriginal,
  formatPeriod,
  formatPrice,
} from "@/lib/pricing";

const BUSINESS_PLAN_READONLY = false;

const makePlans = (language: import("@/lib/language").AppLanguage, billing: BillingPeriod) => {
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });

  return [
    {
      id: "free" as PlanId,
      name: "Free",
      categoryLabel: t("Para começar", "Get started"),
      tagline: t("O essencial, sempre seu.", "The essentials, always yours."),
      price: formatPrice("free", language, billing),
      period: formatPeriod("free", language, billing),
      description: t("Comece grátis, sem limites", "Start free, no limits"),
      features: [
        t("1 página bio", "1 bio page"),
        t("Links ilimitados", "Unlimited links"),
        t("Temas básicos", "Basic themes"),
        t("URL qlivar.com/usuario", "qlivar.com/username URL"),
        t("Analytics básico", "Basic analytics"),
        t("Suporte padrão", "Standard support"),
      ],
      aiFeature: t("Agente IA — uso muito limitado", "AI Agent — very limited"),
      cta: t("Começar grátis", "Start for Free"),
      variant: "hero" as const,
      popular: false,
    },
    {
      id: "starter" as PlanId,
      name: t("Básico", "Starter"),
      categoryLabel: t("Mais popular", "Most popular"),
      tagline: t("O salto que muda o jogo.", "The leap that changes the game."),
      price: formatPrice("starter", language, billing),
      period: formatPeriod("starter", language, billing),
      description: t("Para creators prontos para crescer", "For creators ready to grow"),
      features: [
        t("Todos os temas", "All themes"),
        t("Cores e fontes personalizadas", "Custom colors & fonts"),
        t("Analytics de cliques", "Click analytics"),
        t("Remove marca Qlivar", "Remove Qlivar branding"),
        t("Miniaturas de link personalizadas", "Custom link thumbnails"),
        t("Coleta de e-mails", "Email collection"),
        t("Suporte prioritário", "Priority support"),
      ],
      aiFeature: t("Agente IA — uso generoso", "AI Agent — generous usage"),
      cta: t("Testar Básico grátis", "Try Starter free"),
      variant: "hero" as const,
      popular: true,
    },
    {
      id: "pro" as PlanId,
      name: t("Profissional", "Professional"),
      categoryLabel: t("Para monetizar", "For monetization"),
      tagline: t("Onde a presença vira receita.", "Where presence becomes revenue."),
      price: formatPrice("pro", language, billing),
      period: formatPeriod("pro", language, billing),
      description: t("Para quem monetiza de verdade", "For pros who monetize"),
      features: [
        t("Tudo do Básico", "Everything in Starter"),
        t("Analytics avançado + insights de audiência", "Advanced analytics + audience insights"),
        t("Mini-loja (venda e receba pagamentos)", "Mini-store (sell & collect payments)"),
        t("Domínio personalizado", "Custom domain"),
        t("Agendamento e expiração de links", "Link scheduling & expiry"),
        t("Testes A/B", "A/B testing"),
      ],
      aiFeature: t("Agente IA — uso avançado", "AI Agent — advanced usage"),
      cta: t("Testar Pro grátis", "Try Pro free"),
      variant: "default" as const,
      popular: false,
    },
    {
      id: "business" as PlanId,
      name: t("Empresarial", "Business"),
      categoryLabel: t("Para escalar com IA", "Scale with AI"),
      tagline: t("Sua marca, com inteligência ilimitada.", "Your brand, with unlimited intelligence."),
      price: formatPrice("business", language, billing),
      period: formatPeriod("business", language, billing),
      description: t("Para times escalando com IA", "For teams scaling with AI"),
      features: [
        t("Tudo do Profissional", "Everything in Pro"),
        t("Link de chatbot especializado", "Standalone chatbot link"),
        t("Qualificação de leads com IA", "AI lead qualification"),
        t("Inteligência de negócios com IA", "AI business intelligence"),
        t("Recursos de IA prioritários", "Priority AI resources"),
        t("Suporte white-glove", "White-glove support"),
      ],
      aiFeature: t("Agente IA completo — sem limites práticos", "Full AI Agent — virtually unlimited"),
      cta: t("Testar Empresarial grátis", "Try Business free"),
      variant: "default" as const,
      popular: false,
    },
  ];
};

type ComparisonRow = {
  label: string;
  free: boolean | string;
  starter: boolean | string;
  pro: boolean | string;
  business: boolean | string;
};

interface PricingSectionProps {
  titlePrefix?: string;
  titleHighlight?: string;
  description?: string;
}

export function PricingSection({
  titlePrefix = "Simple pricing.",
  titleHighlight = "Real value.",
  description = "Start free with your bio page and basic analytics. Upgrade when you need more power.",
}: PricingSectionProps) {
  const { language } = useLanguage();
  const rm = useReducedMotion();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const { user } = useAuth();
  const {
    planId,
    createCheckout,
    createGuestCheckout,
    openCustomerPortal,
    hasStripeCustomer,
    loading: subLoading,
    isCheckoutLoading,
    checkoutLoadingPlanId,
  } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const authPath = `/auth${location.search}`;
  const isPricingPage = location.pathname === "/pricing";

  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const effectiveBilling: BillingPeriod = billing;
  const plans = makePlans(language, effectiveBilling);

  // Carousel mobile — starts at 0 (DOM initial state), scrolls to popular after layout
  const POPULAR_INDEX = plans.findIndex((p) => p.popular);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const billingMonthlyRef = useRef<HTMLButtonElement>(null);
  const billingAnnualRef = useRef<HTMLButtonElement>(null);
  const [billingIndicator, setBillingIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const targetIdx = POPULAR_INDEX >= 0 ? POPULAR_INDEX : 0;
    if (!container || targetIdx === 0) return;
    // double-rAF: ensures layout is fully computed before measuring offsetWidth
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const card = container.children[targetIdx] as HTMLElement | undefined;
        if (!card || !container.offsetWidth) return;
        container.scrollLeft = card.offsetLeft - (container.offsetWidth - card.offsetWidth) / 2;
        setActiveIndex(targetIdx);
      });
    });
    // roda apenas uma vez na montagem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const btn = effectiveBilling === "monthly" ? billingMonthlyRef.current : billingAnnualRef.current;
    if (!btn) return;
    setBillingIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [effectiveBilling]);

  const scrollToCard = useCallback((index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const card = container.children[index] as HTMLElement | undefined;
    if (!card) return;
    const left = card.offsetLeft - (container.offsetWidth - card.offsetWidth) / 2;
    container.scrollTo({ left, behavior: "smooth" });
    setActiveIndex(index);
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const center = container.scrollLeft + container.offsetWidth / 2;
    let closest = 0;
    let minDist = Infinity;
    Array.from(container.children).forEach((child, i) => {
      const el = child as HTMLElement;
      const dist = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    setActiveIndex(closest);
  }, []);

  const [portalLoadingForPlanId, setPortalLoadingForPlanId] = useState<string | null>(null);
  const [downgradeDialog, setDowngradeDialog] = useState<{
    open: boolean;
    planActionId: string;
    planName: string;
  }>({
    open: false,
    planActionId: "",
    planName: "",
  });

  useEffect(() => {
    trackPricingInteraction("view");
  }, []);

  const planOrder: PlanId[] = ["free", "starter", "pro", "business"];

  const comparisonGroups: Array<{ name: string; rows: ComparisonRow[] }> = [
    {
      name: t("Página", "Page"),
      rows: [
        { label: t("Links ilimitados", "Unlimited links"), free: true, starter: true, pro: true, business: true },
        { label: t("Temas premium", "Premium themes"), free: false, starter: true, pro: true, business: true },
        { label: t("Marca removida", "No Qlivar branding"), free: false, starter: true, pro: true, business: true },
      ],
    },
    {
      name: "Analytics",
      rows: [
        { label: t("Analytics básico", "Basic analytics"), free: true, starter: true, pro: true, business: true },
        { label: t("Insights avançados", "Advanced insights"), free: false, starter: false, pro: true, business: true },
        { label: t("Testes A/B", "A/B tests"), free: false, starter: false, pro: true, business: true },
      ],
    },
    {
      name: t("Monetização", "Monetization"),
      rows: [
        { label: t("Coleta de leads", "Lead capture"), free: false, starter: true, pro: true, business: true },
        { label: t("Mini-store", "Mini-store"), free: false, starter: false, pro: true, business: true },
        { label: t("Domínio personalizado", "Custom domain"), free: false, starter: false, pro: true, business: true },
      ],
    },
    {
      name: t("IA", "AI"),
      rows: [
        { label: t("Agente IA na bio", "AI agent on the page"), free: true, starter: true, pro: true, business: true },
        { label: t("Link de chatbot dedicado", "Dedicated chatbot link"), free: false, starter: false, pro: false, business: true },
        { label: t("Qualificação de leads", "Lead qualification"), free: false, starter: false, pro: false, business: true },
      ],
    },
  ];

  const openPortalForPlan = async (planActionId: string) => {
    setPortalLoadingForPlanId(planActionId);
    try {
      await openCustomerPortal();
    } finally {
      setPortalLoadingForPlanId(null);
    }
  };

  const handlePlanAction = async (plan: (typeof plans)[number]) => {
    if (BUSINESS_PLAN_READONLY && plan.id === "business") {
      return;
    }

    trackPricingInteraction("click_plan", plan.id as "free" | "starter" | "pro" | "business");
    trackCTAClick(getButtonText(plan), "pricing_section", plan.id === "free" ? "/dashboard" : user ? "checkout" : authPath);

    if (!user) {
      if (plan.id === "free") {
        navigate(authPath);
        return;
      }
      await createGuestCheckout(plan.id as "starter" | "pro" | "business", effectiveBilling);
      return;
    }

    if (planId === plan.id) {
      if (plan.id === "free") {
        navigate("/dashboard");
      } else if (hasStripeCustomer) {
        // Existing Stripe subscriber — open management portal
        await openPortalForPlan(plan.id);
      }
      // No Stripe customer means plan was set manually (e.g. promo/admin).
      // Do nothing — button is visually disabled for this case.
      return;
    }

    const currentIndex = planOrder.indexOf(planId);
    const targetIndex = planOrder.indexOf(plan.id as PlanId);

    if (targetIndex > currentIndex) {
      await createCheckout(plan.id as "starter" | "pro" | "business", effectiveBilling);
    } else {
      setDowngradeDialog({ open: true, planActionId: plan.id, planName: plan.name });
    }
  };

  const getButtonText = (plan: (typeof plans)[number]) => {
    if (checkoutLoadingPlanId === plan.id) return t("Abrindo checkout...", "Opening checkout...");
    if (portalLoadingForPlanId === plan.id) return t("Abrindo portal...", "Opening portal...");
    if (!user) return plan.cta;
    if (planId === plan.id) {
      if (plan.id === "free") return t("Ir ao Dashboard", "Go to Dashboard");
      return hasStripeCustomer
        ? t("Gerenciar assinatura", "Manage subscription")
        : t("Plano atual", "Current plan");
    }
    if (plan.id === "free") return t("Downgrade para Free", "Downgrade to Free");

    const currentIndex = planOrder.indexOf(planId);
    const targetIndex = planOrder.indexOf(plan.id as PlanId);

    return targetIndex > currentIndex
      ? t(`Upgrade para ${plan.name}`, `Upgrade to ${plan.name}`)
      : t(`Downgrade para ${plan.name}`, `Downgrade to ${plan.name}`);
  };

  const renderTableCell = (value: boolean | string) => {
    if (value === true) {
      return (
        <div className="flex justify-center">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#ffe6d3] text-[#ef7b43]">
            <Check className="h-3.5 w-3.5" />
          </div>
        </div>
      );
    }

    if (value === false) {
      return (
        <div className="flex justify-center">
          <Minus className="h-4 w-4 text-[#b6a59a]" />
        </div>
      );
    }

    return <span className="text-xs text-[#493c34]">{value}</span>;
  };

  return (
    <section id="pricing" className="bg-[#fff7ef] py-14 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6">
        <motion.div
          initial={rm ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: rm ? 0 : 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-[#9a7258]">
            {pickByLanguage(language, { pt: "Pricing", en: "Pricing" })}
          </p>
          <h2 className="mt-4 font-editorial font-semibold text-[clamp(2rem,6vw,4rem)] leading-[1.05] sm:leading-[1.02] tracking-[-0.035em] text-[#201814]">
            {t("Preços simples.", titlePrefix)}{" "}
            <span className="text-gradient-creator">{t("Valor real.", titleHighlight)}</span>
          </h2>
          <p className="mt-3 text-[15px] leading-7 text-[#66584d] sm:mt-5 sm:text-lg sm:leading-8">
            {t(
              "Comece com o plano gratuito e suba de nível só quando a operação pedir mais loja, domínio ou IA.",
              description,
            )}
          </p>

          {/* Trust strip — premium reassurance */}
          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[#7a6a5f] sm:mt-7 sm:text-[12px]">
            <li className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[#ef7b43]" />
              {t("7 dias grátis", "7-day free trial")}
            </li>
            <li className="hidden h-1 w-1 rounded-full bg-[#d9cec5] sm:inline-block" aria-hidden />
            <li className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-[#ef7b43]" />
              {t("Sem cartão para começar", "No card to start")}
            </li>
            <li className="hidden h-1 w-1 rounded-full bg-[#d9cec5] sm:inline-block" aria-hidden />
            <li className="inline-flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-[#ef7b43]" />
              {t("Cancele quando quiser", "Cancel anytime")}
            </li>
          </ul>
        </motion.div>

        {/* Billing toggle — refined segmented control with ref-measured indicator */}
        <div className="mt-7 flex flex-col items-center gap-3 sm:mt-10">
          <div
            role="tablist"
            aria-label={t("Período de cobrança", "Billing period")}
            className="relative inline-flex items-center rounded-full border border-[#e6d4c2] bg-white p-[3px] shadow-[0_1px_4px_rgba(32,24,20,0.06),0_2px_12px_rgba(32,24,20,0.04)]"
          >
            {/* Sliding indicator — ref-measured for pixel-perfect tracking */}
            <span
              aria-hidden
              className="pointer-events-none absolute top-[3px] bottom-[3px] rounded-full bg-[#201814] shadow-[0_1px_6px_rgba(32,24,20,0.16)]"
              style={{
                left: billingIndicator?.left ?? 3,
                width: billingIndicator?.width ?? "calc(50% - 2px)",
                transition: billingIndicator
                  ? "left 260ms cubic-bezier(0.4,0,0.2,1), width 260ms cubic-bezier(0.4,0,0.2,1)"
                  : "none",
                opacity: billingIndicator ? 1 : 0,
              }}
            />
            <button
              ref={billingMonthlyRef}
              type="button"
              role="tab"
              aria-selected={effectiveBilling === "monthly"}
              onClick={() => setBilling("monthly")}
              className={cn(
                "relative z-10 rounded-full px-[15px] py-[7px]",
                "text-[13px] font-semibold leading-none tracking-[-0.015em]",
                "transition-colors duration-200 select-none",
                effectiveBilling === "monthly" ? "text-white" : "text-[#8a7568] hover:text-[#3d2e26]",
              )}
            >
              {t("Mensal", "Monthly")}
            </button>
            <button
              ref={billingAnnualRef}
              type="button"
              role="tab"
              aria-selected={effectiveBilling === "annual"}
              onClick={() => setBilling("annual")}
              className={cn(
                "relative z-10 inline-flex items-center gap-[6px] rounded-full px-[15px] py-[7px]",
                "text-[13px] font-semibold leading-none tracking-[-0.015em]",
                "transition-colors duration-200 select-none",
                effectiveBilling === "annual" ? "text-white" : "text-[#8a7568] hover:text-[#3d2e26]",
              )}
            >
              {t("Anual", "Annual")}
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-[7px] py-[3px]",
                  "text-[10px] font-bold tracking-[0.02em] leading-none",
                  "transition-colors duration-200",
                  effectiveBilling === "annual"
                    ? "bg-white/20 text-white"
                    : "bg-[#fde8d4] text-[#bf5924]",
                )}
              >
                −20%
              </span>
            </button>
          </div>
          <span
            className={cn(
              "text-[11px] font-medium tracking-[0.01em] transition-colors duration-300",
              effectiveBilling === "annual" ? "text-[#ef7b43]" : "text-[#b5a49a]",
            )}
          >
            {t("Economize 2 meses pagando anualmente", "Save 2 months when billed annually")}
          </span>
        </div>

        {(() => {
          const renderCard = (plan: (typeof plans)[number]) => {
            const isCurrentPlan = !!user && planId === plan.id;
            const visibleFeatures = plan.features.slice(0, 5);
            const isPopular = plan.popular && !isCurrentPlan;
            const buttonVariant = isCurrentPlan ? "outline" : plan.popular ? "hero" : plan.variant;
            const theme = TIER_THEME[plan.id as keyof typeof TIER_THEME];
            const TierIcon = theme.Icon;
            const isBusiness = plan.id === "business";
            const isPro = plan.id === "pro";

            return (
              <article
                className={cn(
                  "group/card relative flex h-full w-full flex-col rounded-[22px] p-4 transition-colors duration-200 ease-out sm:rounded-[28px] sm:p-6",
                  isPopular
                    ? `border-2 border-[#ef7b43] bg-white ${CARD_SHADOW_POPULAR} sm:shadow-[0_24px_60px_-18px_rgba(239,123,67,0.45),0_8px_24px_rgba(239,123,67,0.12)]`
                    : isCurrentPlan
                    ? "border border-[#1f1a18]/60 bg-[#fffaf5] shadow-[0_8px_20px_rgba(110,79,52,0.06)]"
                    : isBusiness
                    ? "border border-[#5b46c4]/25 bg-gradient-to-b from-white to-[#faf7ff] shadow-[0_8px_20px_rgba(91,70,196,0.08)] hover:border-[#5b46c4]/45 hover:shadow-[0_18px_40px_rgba(91,70,196,0.16)]"
                    : isPro
                    ? "border border-[#b8842a]/25 bg-gradient-to-b from-white to-[#fffaf0] shadow-[0_8px_20px_rgba(184,132,42,0.07)] hover:border-[#b8842a]/45 hover:shadow-[0_18px_40px_rgba(184,132,42,0.14)]"
                    : "border border-[#eadbcc] bg-white shadow-[0_8px_20px_rgba(110,79,52,0.06)] hover:border-[#ef7b43]/40 hover:shadow-[0_14px_30px_rgba(110,79,52,0.10)]",
                )}
              >
                {/* Premium ambient halo for popular card */}
                {isPopular && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -inset-px hidden rounded-[28px] sm:block"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(255,184,124,0.18), transparent 55%)",
                      mask: "linear-gradient(#000, #000) content-box, linear-gradient(#000, #000)",
                      WebkitMask:
                        "linear-gradient(#000, #000) content-box, linear-gradient(#000, #000)",
                      maskComposite: "exclude",
                      WebkitMaskComposite: "xor",
                      padding: "1px",
                    }}
                  />
                )}

                {isPopular && (
                  <span className="absolute left-1/2 -top-3.5 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-gradient-to-r from-[#ef7b43] to-[#f0935f] px-4 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_8px_22px_rgba(239,123,67,0.45)]">
                    <Check className="h-3 w-3" />
                    {t("Mais escolhido", "Most chosen")}
                  </span>
                )}

                {!isPopular && isBusiness && !isCurrentPlan && (
                  <span className="absolute left-1/2 -top-3.5 inline-flex -translate-x-1/2 items-center gap-1 rounded-full border border-[#5b46c4]/20 bg-white px-4 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-[#5b46c4] shadow-[0_6px_14px_rgba(91,70,196,0.15)]">
                    <Gem className="h-3 w-3" />
                    {t("Concierge IA", "AI Concierge")}
                  </span>
                )}

                {isCurrentPlan && (
                  <span className="absolute left-1/2 -top-3.5 inline-flex -translate-x-1/2 rounded-full bg-[#1f1a18] px-4 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-white shadow-lg">
                    {t("Plano atual", "Current plan")}
                  </span>
                )}

                <div className="border-b border-[#f0e3d7] pb-3 sm:pb-5">
                  <p
                    className={cn(
                      "inline-flex items-center gap-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.22em] sm:text-[0.68rem] sm:tracking-[0.24em]",
                      theme.eyebrow,
                    )}
                  >
                    <TierIcon className="h-3 w-3" strokeWidth={2.5} />
                    {plan.categoryLabel}
                  </p>
                  <h3 className="mt-2.5 font-editorial font-semibold text-[1.4rem] leading-[1.05] tracking-[-0.03em] text-[#1f1713] sm:mt-3 sm:text-[2.1rem] sm:tracking-[-0.04em]">
                    {plan.name}
                  </h3>
                  <p className="mt-2 text-[0.78rem] leading-[1.4] text-[#6b5d53] sm:mt-3 sm:min-h-[52px] sm:text-sm sm:leading-6">
                    {plan.description}
                  </p>

                  <div className="mt-3 flex items-end gap-2 sm:mt-5">
                    <span className="font-editorial font-semibold text-[1.95rem] leading-none tracking-[-0.045em] tabular-nums text-[#201814] sm:text-[3.15rem] sm:tracking-[-0.055em]">
                      {plan.price}
                    </span>
                    <span className="pb-1 text-xs text-[#7a6a5f] sm:text-sm">{plan.period}</span>
                  </div>

                  <div className="mt-2 text-[11px] leading-[1.4] text-[#7a6a5f] sm:mt-3 sm:min-h-[2.75rem] sm:text-xs sm:leading-[1.5]">
                    {effectiveBilling === "annual" && plan.id !== "free" ? (
                      <>
                        <p>
                          <span className="line-through opacity-70">
                            {formatMonthlyOriginal(plan.id, language)}
                          </span>{" "}
                          ·{" "}
                          <span className="font-semibold text-[#c25b27]">
                            {t("economize", "save")} {formatAnnualSavings(plan.id, language)}
                          </span>
                        </p>
                        <p>{formatAnnualTotal(plan.id, language)}</p>
                      </>
                    ) : plan.id === "free" ? (
                      <p>{t("Grátis para sempre", "Free forever")}</p>
                    ) : (
                      <p>{t("Teste grátis por 7 dias", "7-day free trial")}</p>
                    )}
                  </div>
                </div>

                <div className="flex-1 pt-3 sm:pt-5">
                  {/* Aspirational tagline — value framing above the list */}
                  <p
                    className={cn(
                      "mb-3 text-[0.78rem] font-semibold leading-snug tracking-[-0.005em] sm:mb-4 sm:text-[0.85rem]",
                      theme.eyebrow,
                    )}
                  >
                    {plan.tagline}
                  </p>

                  <ul className="space-y-2 sm:space-y-2.5">
                    {visibleFeatures.map((feature, i) => {
                      const isInheritance = i === 0 && plan.id !== "free" && plan.id !== "starter";
                      return (
                        <li
                          key={feature}
                          className={cn(
                            "flex items-start gap-2.5 leading-[1.45] sm:gap-3 sm:leading-6",
                            isInheritance
                              ? "text-[0.78rem] font-semibold text-[#201814] sm:text-sm"
                              : "text-[0.8rem] text-[#312823] sm:text-sm",
                          )}
                        >
                          <span
                            className={cn(
                              "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                              theme.chipBg,
                              theme.chipText,
                            )}
                          >
                            <Check className="h-3 w-3" strokeWidth={2.5} />
                          </span>
                          <span>{feature}</span>
                        </li>
                      );
                    })}
                    <li className="flex items-start gap-2.5 text-[0.8rem] leading-[1.45] text-[#312823] sm:gap-3 sm:text-sm sm:leading-6">
                      <span
                        className={cn(
                          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                          theme.aiChipBg,
                          theme.aiChipText,
                        )}
                      >
                        <Bot className="h-3 w-3" strokeWidth={2.5} />
                      </span>
                      <span>{plan.aiFeature}</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-4 sm:pt-6">
                  <Button
                    variant={buttonVariant}
                    size="lg"
                    className={cn(
                      "h-11 w-full rounded-full text-sm font-semibold transition-[transform,background-color,border-color,color,box-shadow] duration-200 ease-out active:scale-[0.97] sm:h-12 sm:text-base",
                      isPopular
                        ? "focus-visible:ring-[#ef7b43]/40 shadow-[0_14px_30px_rgba(239,123,67,0.32)] hover:shadow-[0_18px_36px_rgba(239,123,67,0.42)]"
                        : isBusiness && !isCurrentPlan
                        ? "bg-gradient-to-br from-[#4a2fd4] via-[#7040ee] to-[#a060ff] text-white shadow-[0_8px_22px_rgba(91,70,196,0.38)] hover:shadow-[0_14px_32px_rgba(112,64,238,0.52)] hover:brightness-110 focus-visible:ring-[#7040ee]/40"
                        : isPro && !isCurrentPlan
                        ? "bg-gradient-to-br from-[#e8a820] via-[#c07818] to-[#96600c] text-white shadow-[0_8px_22px_rgba(184,132,42,0.38)] hover:shadow-[0_14px_32px_rgba(192,120,24,0.50)] hover:brightness-110 focus-visible:ring-[#c07818]/40"
                        : "focus-visible:ring-[#ef7b43]/40",
                    )}
                    onClick={() => handlePlanAction(plan)}
                    isLoading={checkoutLoadingPlanId === plan.id || portalLoadingForPlanId === plan.id}
                    disabled={
                      // Current plan with no Stripe customer: no action available
                      (isCurrentPlan && plan.id !== "free" && !hasStripeCustomer) ||
                      BUSINESS_PLAN_READONLY ||
                      subLoading ||
                      (isCheckoutLoading && checkoutLoadingPlanId !== plan.id) ||
                      (portalLoadingForPlanId !== null && portalLoadingForPlanId !== plan.id)
                    }
                  >
                    {getButtonText(plan)}
                  </Button>
                  {plan.id !== "free" && (
                    <p className="mt-2.5 text-center text-[11px] text-[#9a8a7d]">
                      {t("Sem cobranças nos primeiros 7 dias", "No charges for the first 7 days")}
                    </p>
                  )}
                  {plan.id === "free" && (
                    <p className="mt-2.5 text-center text-[11px] font-medium text-emerald-600">
                      {t("Completamente grátis", "Completely free")}
                    </p>
                  )}
                </div>
              </article>
            );
          };

          return (
            <>
              {/* Mobile carousel — snap-x + setas + dots */}
              <div className="mt-8 md:hidden">
                <div className="-mx-4">
                  {/* Setas posicionadas relativas apenas à track de scroll */}
                  <div className="relative">
                    <CarouselArrow
                      direction="left"
                      onClick={() => scrollToCard(activeIndex - 1)}
                      disabled={activeIndex === 0}
                      ariaLabel={t("Plano anterior", "Previous plan")}
                    />

                    <CarouselArrow
                      direction="right"
                      onClick={() => scrollToCard(activeIndex + 1)}
                      disabled={activeIndex === plans.length - 1}
                      ariaLabel={t("Próximo plano", "Next plan")}
                    />

                    {/* Edge fade masks */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-8 z-10 bg-gradient-to-r from-[#fff7ef] to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-8 z-10 bg-gradient-to-l from-[#fff7ef] to-transparent" />

                    <div
                      ref={scrollRef}
                      onScroll={handleScroll}
                      className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-[calc(50%-9rem)] py-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                      {plans.map((plan) => (
                        <div
                          key={plan.id}
                          className="w-[78vw] max-w-[18rem] flex-shrink-0 snap-center"
                        >
                          {renderCard(plan)}
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>

              <CarouselDots
                count={plans.length}
                activeIndex={activeIndex}
                onDotClick={scrollToCard}
                ariaLabelFn={(i) => t(`Ver plano ${i + 1}`, `View plan ${i + 1}`)}
                className="md:hidden"
              />

              {/* Desktop grid */}
              <div className="mt-14 hidden items-stretch gap-5 md:grid md:grid-cols-2 xl:grid-cols-4">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={rm ? false : { opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: rm ? 0 : 0.45, delay: index * 0.08 }}
                    className={cn("flex h-full flex-col", plan.popular && !(!!user && planId === plan.id) && "lg:-translate-y-3")}
                  >
                    {renderCard(plan)}
                  </motion.div>
                ))}
              </div>
            </>
          );
        })()}

        {isPricingPage ? (
          <motion.div
            initial={rm ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: rm ? 0 : 0.45, delay: 0.15 }}
            className="mt-16 overflow-hidden rounded-[28px] border border-[#eadbcc] bg-white shadow-[0_26px_65px_rgba(110,79,52,0.08)]"
          >
            <div className="border-b border-[#f0e3d7] px-6 py-5">
              <p className="text-[0.66rem] font-semibold uppercase tracking-[0.24em] text-[#9a7258]">
                {t("Comparativo completo", "Full comparison")}
              </p>
              <h3 className="mt-1.5 font-editorial text-[2.1rem] leading-none tracking-[-0.04em] text-[#201814]">
                {t("Compare todos os recursos", "Compare every feature")}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[640px] w-full text-sm">
                <thead>
                  <tr className="border-b border-[#f0e3d7] bg-[#fffaf5] text-[#201814]">
                    <th className="p-4 text-left font-semibold" />
                    <th className="p-4 text-center font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        <Star className="h-3.5 w-3.5 text-[#9a8a7d]" strokeWidth={2.5} />
                        Free
                      </span>
                    </th>
                    <th className="p-4 text-center font-semibold">
                      <span className="inline-flex items-center gap-1.5 text-[#ef7b43]">
                        <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Starter
                      </span>
                    </th>
                    <th className="p-4 text-center font-semibold">
                      <span className="inline-flex items-center gap-1.5 text-[#a87324]">
                        <Crown className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Professional
                      </span>
                    </th>
                    <th className="p-4 text-center font-semibold">
                      <span className="inline-flex items-center gap-1.5 text-[#5b46c4]">
                        <Gem className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Business
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonGroups.flatMap((group, groupIndex) => [
                    <tr key={`group-${groupIndex}`} className="bg-[#fff7ef]">
                      <td colSpan={5} className="px-4 py-3 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-[#9a7258]">
                        {group.name}
                      </td>
                    </tr>,
                    ...group.rows.map((row) => (
                      <tr key={`${group.name}-${row.label}`} className="border-t border-[#f6ebe1] text-[#493c34]">
                        <td className="px-4 py-4">{row.label}</td>
                        <td className="px-4 py-4 text-center">{renderTableCell(row.free)}</td>
                        <td className="px-4 py-4 text-center">{renderTableCell(row.starter)}</td>
                        <td className="px-4 py-4 text-center">{renderTableCell(row.pro)}</td>
                        <td className="px-4 py-4 text-center">{renderTableCell(row.business)}</td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate("/pricing#pricing")}
              className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6f6055] transition-colors hover:text-[#201814]"
            >
              {t("Compare todos os recursos", "Compare all features")}
            </button>
          </div>
        )}

        <div className="mt-10 flex flex-col items-center gap-3">
          <div className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-full border border-[#eadbcc] bg-white/60 px-5 py-2.5 text-[11px] font-medium text-[#76675b] shadow-[0_4px_14px_rgba(110,79,52,0.05)] sm:text-[12px]">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[#ef7b43]" strokeWidth={2.5} />
              {t("Pagamento seguro Stripe", "Secure Stripe payments")}
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-[#d9cec5] sm:inline-block" aria-hidden />
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-[#ef7b43]" strokeWidth={2.5} />
              {t("Garantia de 30 dias", "30-day guarantee")}
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-[#d9cec5] sm:inline-block" aria-hidden />
            <span className="inline-flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-[#ef7b43]" strokeWidth={2.5} />
              {t("Infraestrutura global", "Global infrastructure")}
            </span>
          </div>
          <p className="max-w-2xl text-center text-sm leading-7 text-[#76675b]">
            {pickByLanguage(language, {
              pt: "Todos os planos incluem SSL, página rápida e infraestrutura global. Você cresce sem trocar de stack no meio do caminho.",
              en: "Every plan includes SSL, fast delivery, and global infrastructure. You grow without changing stack halfway through.",
            })}
          </p>
        </div>
      </div>

      <CancelDowngradeDialog
        open={downgradeDialog.open}
        onOpenChange={(open) => setDowngradeDialog((prev) => ({ ...prev, open }))}
        onConfirm={() => openPortalForPlan(downgradeDialog.planActionId)}
        type="downgrade"
        targetPlanName={downgradeDialog.planName}
        language={language}
      />
    </section>
  );
}
